// ═══════════════════════════════════════════════════════════════════════════
// SafeHTML — XSS-safe HTML rendering wrapper
//
// Replaces direct `dangerouslySetInnerHTML` calls. All HTML is run through
// DOMPurify before being injected, blocking script tags, javascript: URLs,
// inline event handlers, and any other vector listed in the OWASP XSS sheet.
//
// Usage:
//   <SafeHTML html={article.content} />               // block-level (div)
//   <SafeHTML html={comment} as="span" />             // inline-level
//   <SafeHTML html={msg} profile="chat" />            // restricted preset
// ═══════════════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import type { Config } from 'dompurify';

/**
 * Profiles enumerate the allowed HTML surface for each calling context.
 * Tightening allowlists per use-case is safer than one-size-fits-all.
 */
const PROFILES = {
  // Rich text from the blog editor: structural tags + inline formatting +
  // links + media. No <script>, no <iframe>, no <form>, no inline event
  // handlers (DOMPurify strips those by default).
  article: {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup', 'mark',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'blockquote', 'pre', 'code',
      'a', 'img', 'figure', 'figcaption',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'hr', 'span', 'div',
    ],
    ALLOWED_ATTR: [
      'href', 'title', 'target', 'rel',
      'src', 'alt', 'width', 'height', 'loading',
      'class', 'id',
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  } satisfies Config,

  // Chat / AI advisor output: same as article but stricter — no images, no
  // tables (LLM output rarely needs them and can hide XSS payloads).
  chat: {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 's',
      'ul', 'ol', 'li',
      'blockquote', 'pre', 'code',
      'a', 'span',
    ],
    ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'class'],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:)/i,
  } satisfies Config,

  // Minimal: text-level formatting only.
  basic: {
    ALLOWED_TAGS: ['strong', 'b', 'em', 'i', 'u', 'br', 'span', 'code'],
    ALLOWED_ATTR: ['class'],
  } satisfies Config,
} as const;

export type SafeHTMLProfile = keyof typeof PROFILES;

interface SafeHTMLProps {
  html: string;
  /** Allow-list profile. Defaults to 'basic' (most restrictive). */
  profile?: SafeHTMLProfile;
  /** Wrapping element. Defaults to 'div'. */
  as?: 'div' | 'span' | 'section' | 'article' | 'p';
  className?: string;
  /** Optional style for ergonomics; avoid in design-system code. */
  style?: React.CSSProperties;
  /** Identifier for testing / a11y. */
  id?: string;
}

/**
 * Force every external link to open with `rel="noopener noreferrer"` and
 * `target="_blank"` after sanitization. Defense in depth — DOMPurify already
 * strips javascript: URIs, this only mitigates tab-nabbing.
 */
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    const href = node.getAttribute('href') || '';
    if (/^https?:/i.test(href)) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  }
});

export function SafeHTML({
  html,
  profile = 'basic',
  as = 'div',
  className,
  style,
  id,
}: SafeHTMLProps) {
  const sanitized = useMemo(() => {
    if (!html) return '';
    return DOMPurify.sanitize(html, PROFILES[profile]);
  }, [html, profile]);

  const Tag = as;

  return (
    <Tag
      id={id}
      className={className}
      style={style}
       
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  );
}

export default SafeHTML;
